(() => {
  "use strict";

  const root = document.querySelector("[data-lr-shop-radio]");
  if (!root || root.dataset.ready === "true") return;
  root.dataset.ready = "true";

  const audio = root.querySelector("[data-lr-shop-radio-audio]");
  const playButton = root.querySelector("[data-lr-shop-radio-play]");
  const nextButton = root.querySelector("[data-lr-shop-radio-next]");
  const title = root.querySelector("[data-lr-shop-radio-title]");
  const album = root.querySelector("[data-lr-shop-radio-album]");
  const cover = root.querySelector("[data-lr-shop-radio-cover]");

  const fallbackCover = cover?.getAttribute("src") || "";
  const DEFAULT_CONTENT_BASE = "https://thelordrazu.github.io/thelordrazu";
  const FALLBACK_AD = {
    type: "ad",
    title: "INEXORABLE",
    album: "THE LORD RAZU RADIO — STATION BREAK",
    src: `${DEFAULT_CONTENT_BASE}/Music/ads/enexorable-ad.mp3`,
    artwork: fallbackCover,
  };

  let songs = [];
  let ads = [];
  let currentItem = null;
  let recentSongIds = [];
  let songsSinceAd = 0;
  let nextAdAt = randomAdInterval();

  const isAbsolute = (value = "") => /^(?:https?:)?\/\//i.test(value);

  const assetUrl = (base, value = "") => {
    if (!value) return "";
    if (isAbsolute(value)) return value;
    return `${String(base || "").replace(/\/+$/, "")}/${String(value).replace(/^\/+/, "")}`;
  };

  function randomAdInterval() {
    return 4 + Math.floor(Math.random() * 3); // 4, 5, or 6 songs
  }

  function setPlayIcon(isPlaying) {
    if (!playButton) return;
    playButton.setAttribute("aria-label", isPlaying ? "Pause radio" : "Play radio");
    playButton.innerHTML = isPlaying
      ? '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="M8 6v12M16 6v12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>'
      : '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="m9 7 8 5-8 5V7Z" fill="currentColor"/></svg>';
  }

  function normalizeCatalog(catalog) {
    const base = String(catalog?.contentBase || DEFAULT_CONTENT_BASE).replace(/\/+$/, "");
    const albums = new Map((catalog?.albums || []).map((item) => [item.key, item]));

    const normalizedSongs = (catalog?.songs || [])
      .filter((song) => song?.playback?.previewRadio === true && song?.preview && !["the-lord-razu-acapella", "the-lord-razu-march-outro"].includes(song?.id))
      .map((song) => {
        const albumRecord = albums.get(song.albumKey) || {};
        return {
          type: "song",
          id: song.id || song.slug || song.title,
          title: song.shortTitle || song.title || "The Lord Razu Radio",
          album: song.album || albumRecord.title || "THE LORD RAZU RADIO",
          src: assetUrl(base, song.preview),
          artwork: assetUrl(base, song.artwork || albumRecord.cover || ""),
        };
      })
      .filter((song) => song.src);

    const normalizedAds = (catalog?.ads || [])
      .map((ad, index) => ({
        type: "ad",
        id: ad.id || `ad-${index + 1}`,
        title: ad.shortTitle || ad.title || "STATION BREAK",
        album: ad.album || "THE LORD RAZU RADIO — STATION BREAK",
        src: assetUrl(base, ad.preview || ad.audio || ad.radio || ""),
        artwork: assetUrl(base, ad.artwork || "") || fallbackCover,
      }))
      .filter((ad) => ad.src);

    if (!normalizedAds.length) normalizedAds.push(FALLBACK_AD);

    return { songs: normalizedSongs, ads: normalizedAds };
  }

  function chooseRandomSong() {
    if (!songs.length) return null;

    const recentSet = new Set(recentSongIds);
    let available = songs.filter((song) => !recentSet.has(song.id));
    if (!available.length) {
      recentSongIds = [];
      available = [...songs];
    }

    const chosen = available[Math.floor(Math.random() * available.length)];
    recentSongIds.push(chosen.id);

    const historyLimit = Math.max(1, Math.min(6, songs.length - 1));
    if (recentSongIds.length > historyLimit) recentSongIds.shift();

    return chosen;
  }

  function chooseRandomAd() {
    if (!ads.length) return FALLBACK_AD;
    return ads[Math.floor(Math.random() * ads.length)];
  }

  function chooseNextItem() {
    if (songsSinceAd >= nextAdAt) {
      songsSinceAd = 0;
      nextAdAt = randomAdInterval();
      return chooseRandomAd();
    }

    const song = chooseRandomSong();
    if (song) songsSinceAd += 1;
    return song;
  }

  function updateMediaMetadata(item) {
    if (!("mediaSession" in navigator) || !item) return;
    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: item.title,
        artist: item.type === "ad" ? "The Lord Razu Radio" : "The Lord Razu",
        album: item.album,
        artwork: item.artwork ? [{ src: item.artwork }] : [],
      });
    } catch (_) {}
  }

  function loadItem(item, autoplay = false) {
    if (!item || !audio) return;
    currentItem = item;

    audio.src = item.src;
    audio.load();
    if (title) title.textContent = item.title;
    if (album) album.textContent = item.album;
    if (cover) cover.src = item.artwork || fallbackCover;

    root.dataset.itemType = item.type;
    updateMediaMetadata(item);

    if (autoplay) {
      audio.play().catch(() => setPlayIcon(false));
    } else {
      setPlayIcon(false);
    }
  }

  function advance(autoplay = false) {
    const next = chooseNextItem();
    if (next) loadItem(next, autoplay);
  }

  playButton?.addEventListener("click", () => {
    if (!currentItem) advance(false);
    if (!audio) return;

    if (audio.paused) {
      audio.play().catch(() => setPlayIcon(false));
    } else {
      audio.pause();
    }
  });

  nextButton?.addEventListener("click", () => advance(!audio?.paused));
  audio?.addEventListener("play", () => setPlayIcon(true));
  audio?.addEventListener("pause", () => setPlayIcon(false));
  audio?.addEventListener("ended", () => advance(true));

  if ("mediaSession" in navigator) {
    try {
      navigator.mediaSession.setActionHandler("play", () => audio?.play());
      navigator.mediaSession.setActionHandler("pause", () => audio?.pause());
      navigator.mediaSession.setActionHandler("nexttrack", () => advance(true));
    } catch (_) {}
  }

  root.classList.add("is-loading");

  const loader = window.LordRazuShop?.getMusicCatalog
    ? window.LordRazuShop.getMusicCatalog({ refresh: true })
    : fetch(`${DEFAULT_CONTENT_BASE}/Music/catalog.json`, { cache: "no-store" }).then((r) => r.json());

  Promise.resolve(loader)
    .then((catalog) => {
      const normalized = normalizeCatalog(catalog);
      songs = normalized.songs;
      ads = normalized.ads;

      if (!songs.length) throw new Error("No preview-radio tracks with preview files found in catalog.");

      advance(false);
      root.classList.remove("is-loading");
    })
    .catch((error) => {
      console.error("[The Lord Razu Shop] Radio failed to load:", error);
      if (title) title.textContent = "RADIO OFFLINE";
      if (album) album.textContent = "THE LORD RAZU RADIO";
      root.classList.remove("is-loading");
    });
})();
